import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useAssignUserRoleMutation, useUnassignUserRoleMutation } from "../../../../app/services/userRole";
import { useGetRolePermissionsQuery, useUpdateRoleMutation, useCreateRoleMutation, useGetRolesQuery, useLazyGetRolesQuery } from "../../../../app/services/roles";
import { useGetDealerActiveAddOnsQuery } from "../../../../app/services/addons";
import { useGetUsersQuery } from "../../../../app/services/user";
import { useGetRoleUsersQuery } from "../../../../app/services/roleUser";
import { DealerRolesTabProps } from "../../../models/dealerRoles";

// Types moved to shared models: target-manager/models/dealerRoles.ts

const DealerRolesTab: React.FC<DealerRolesTabProps> = ({ dealerId, dmsDealerId, subscriptions }) => {
  const productTabs = React.useMemo(() => subscriptions.map((s) => ({ id: s.productId, name: s.productName })), [subscriptions]);
  const [activeProductId, setActiveProductId] = React.useState<string>(productTabs[0]?.id ?? "");
  const [roleCounts, setRoleCounts] = React.useState<Record<string, number>>({});
  const [triggerGetRoles] = useLazyGetRolesQuery();

  React.useEffect(() => {
    if (!activeProductId && productTabs.length) setActiveProductId(productTabs[0].id);
    // If current active product is no longer present (e.g., subscriptions changed), reset to first
    if (activeProductId && !productTabs.some(p => p.id === activeProductId)) {
      setActiveProductId(productTabs[0]?.id ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTabs]);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!dmsDealerId || productTabs.length === 0) {
        setRoleCounts({});
        return;
      }
      try {
        const results = await Promise.all(productTabs.map(p =>
          triggerGetRoles({ productId: p.id, dmsDealerId, page: 1, pageSize: 200 }, true)
        ));
        if (cancelled) return;
        const next: Record<string, number> = {};
        results.forEach((r, idx) => {
          const payload: any = r.data as any;
          const list = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
          next[productTabs[idx].id] = (list?.length ?? 0);
        });
        setRoleCounts(next);
      } catch {
        if (!cancelled) setRoleCounts({});
      }
    };
    run();
    return () => { cancelled = true; };
  }, [dmsDealerId, productTabs, triggerGetRoles]);

  if (!activeProductId) {
    return <Typography variant="body2" color="text.secondary">Dealer is not onboarded to any product.</Typography>;
  }

  return (
    <Box>
      <Tabs
        value={activeProductId}
        onChange={(_e, v) => setActiveProductId(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {productTabs.map((p) => (
          <Tab key={p.id} value={p.id} label={`${p.name}${typeof roleCounts[p.id] === 'number' ? ` (${roleCounts[p.id]})` : ''}`} />
        ))}
      </Tabs>

      <ProductRoleManager
        key={activeProductId}
        dealerId={dealerId}
        dmsDealerId={dmsDealerId}
        productId={activeProductId}
      />
    </Box>
  );
};

const ProductRoleManager: React.FC<{ dealerId: number; dmsDealerId?: number; productId: string }> = ({ dealerId, dmsDealerId, productId }) => {
  // Fetch full role catalog for this product + dmsDealerId
  const { data: rolesResp, isFetching: rolesLoading, refetch: refetchRoles } = useGetRolesQuery(
    { productId, dmsDealerId, page: 1, pageSize: 200 },
    { skip: !productId || !dmsDealerId }
  );
  const rawRoles: any[] = (rolesResp as any)?.data ?? (rolesResp as any) ?? [];
  const roles: CreateRoleResponse[] = rawRoles.map((r: any) => ({
    id: r.id ?? r.roleId,
    name: r.name ?? r.roleName,
    normalizedName: r.normalizedName,
    dmsDealerId: r.dmsDealerId,
  }));

  const [search, setSearch] = React.useState("");
  const filteredRoles = React.useMemo(() => roles.filter(r => r.name?.toLowerCase().includes(search.toLowerCase())), [roles, search]);

  const [selectedRoleId, setSelectedRoleId] = React.useState<string | null>(null);
  const [createRole] = useCreateRoleMutation();
  const [newOpen, setNewOpen] = React.useState(false);
  const [newName, setNewName] = React.useState("");

  const selectedRole = roles.find(r => r.id === selectedRoleId) ?? null;

  // Auto-select first available role when roles load or product changes
  React.useEffect(() => {
    if (!rolesLoading && roles.length > 0) {
      setSelectedRoleId((curr) => roles.some(r => r.id === curr) ? curr : roles[0].id);
    }
  }, [rolesLoading, roles]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField
              size="small"
              placeholder="Search roles"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => setNewOpen(true)}
            >
              New
            </Button>
          </Stack>
          <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle>Create New Role</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Role Name"
                type="text"
                fullWidth
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setNewOpen(false); setNewName(""); }}>Cancel</Button>
              <Button
                variant="contained"
                disabled={!newName.trim() || !dmsDealerId}
                onClick={async () => {
                  if (!newName.trim() || !dmsDealerId) return;
                  try {
                    const resp = await createRole({ name: newName.trim(), productId, dmsDealerId }).unwrap();
                    setNewOpen(false);
                    setNewName("");
                    // Try to select newly created role if API returns it
                    if (resp && (resp as any).id) {
                      setSelectedRoleId((resp as any).id);
                    }
                    await refetchRoles();
                  } catch {}
                }}
              >
                Create
              </Button>
            </DialogActions>
          </Dialog>
          {rolesLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading roles…</Typography>
            </Box>
          ) : (
            <List dense disablePadding sx={{ maxHeight: 420, overflow: 'auto' }}>
              {filteredRoles.map((r) => (
                <ListItem key={r.id} disablePadding>
                  <ListItemButton selected={r.id === selectedRoleId} onClick={() => setSelectedRoleId(r.id)}>
                    <ListItemText primary={r.name} secondary={r.normalizedName ? `Key: ${r.normalizedName}` : undefined} />
                  </ListItemButton>
                </ListItem>
              ))}
              {!filteredRoles.length && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No roles found</Typography>
              )}
            </List>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={8}>
        <RoleEditor
          dealerId={dealerId}
          productId={productId}
          role={selectedRole}
          onSaved={() => { refetchRoles(); }}
        />
      </Grid>
    </Grid>
  );
};

const RoleEditor: React.FC<{ dealerId: number; productId: string; role: CreateRoleResponse | null; onSaved: () => void }> = ({ dealerId, productId, role, onSaved }) => {
  const [name, setName] = React.useState<string>(role?.name ?? "");
  React.useEffect(() => setName(role?.name ?? ""), [role?.id]);

  const permArgs = React.useMemo(() => (role ? { roleId: role.id, productId, includeFeatures: true } : null), [role?.id, productId]);
  const { data: permResp, isFetching: permsLoading, refetch: refetchPerms } = useGetRolePermissionsQuery(
    // @ts-ignore safe due to skip when permArgs null
    permArgs,
    { skip: !permArgs }
  );
  const permissions: RolePermissionResponse[] = permResp ?? [];

  const { data: activeAddOns } = useGetDealerActiveAddOnsQuery({ dealerId, productId }, { skip: !dealerId || !productId });
  const allowedFeatureIds = new Set<string>(activeAddOns?.activeFeatureIds ?? []);

  const [updateRole, { isLoading: saving }] = useUpdateRoleMutation();

  // Users assignment
  const [assignUserRole, { isLoading: assigning }] = useAssignUserRoleMutation();
  const [unassignUserRole, { isLoading: unassigning }] = useUnassignUserRoleMutation();
  const { data: usersResp } = useGetUsersQuery({ dealerId, page: 1, pageSize: 200 });
  const dealerUsers = (usersResp?.data as UserInformation[]) ?? [];

  const [assignedUsers, setAssignedUsers] = React.useState<Record<string, boolean>>({});
  const [assignedUsersLoaded, setAssignedUsersLoaded] = React.useState(false);

  // Fetch current users for role via RTK: GET /api/v1/UserRole/{roleId}/users
  const { data: roleUsersResp, isFetching: roleUsersLoading } = useGetRoleUsersQuery(role?.id ?? "", { skip: !role?.id });

  React.useEffect(() => {
    setAssignedUsersLoaded(false);
    setAssignedUsers({});
    if (!role || roleUsersLoading) return;
    const map: Record<string, boolean> = {};
    const arr = (roleUsersResp as any)?.data ?? roleUsersResp ?? [];
    arr.forEach((u: any) => { if (u?.id) map[String(u.id)] = true; });
    setAssignedUsers(map);
    setAssignedUsersLoaded(true);
  }, [role?.id, roleUsersLoading, roleUsersResp]);

  const [permState, setPermState] = React.useState<Record<string, boolean>>({});
  React.useEffect(() => {
    const next: Record<string, boolean> = {};
    (permResp ?? []).forEach((p: any) => { next[p.productFeatureId] = !!p.permission; });
    const curr = permState;
    const keys = new Set([...Object.keys(curr), ...Object.keys(next)]);
    let changed = false;
    keys.forEach(k => { if ((curr[k] ?? false) !== (next[k] ?? false)) changed = true; });
    if (changed) setPermState(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permResp]);

  // User search and filter state
  const [userSearch, setUserSearch] = React.useState("");
  const [userFilter, setUserFilter] = React.useState<"all" | "assigned" | "unassigned">("all");
  const visibleUsers = React.useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    return dealerUsers.filter(u => {
      const assigned = !!assignedUsers[String(u.id)];
      if (userFilter === "assigned" && !assigned) return false;
      if (userFilter === "unassigned" && assigned) return false;
      if (!q) return true;
      const hay = `${u.fullName ?? ''} ${u.userName ?? ''} ${u.email ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [dealerUsers, assignedUsers, userSearch, userFilter]);

  if (!role) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Typography variant="body2" color="text.secondary">Select a role to view and edit details.</Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">{role.name}</Typography>
          <Chip size="small" variant="outlined" color="primary" label={`Assigned: ${Object.values(assignedUsers).filter(Boolean).length}`} />
        </Stack>
        <Button
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          disabled={saving}
          onClick={async () => {
            try {
              const rolePermissions: RolePermissions[] = Object.keys(permState).map(id => ({ productFeatureId: id, permission: !!permState[id], productId }));
              await updateRole({ roleId: role.id, updatedRoleObj: { name: name?.trim() || role.name, rolePermissions } }).unwrap();
              await refetchPerms();
              onSaved();
            } catch {}
          }}
        >
          Save Changes
        </Button>
      </Stack>
      <Divider sx={{ my: 1.5 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>Details</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, mb: 0.5 }}>
            <Typography variant="subtitle2">Features</Typography>
            <Typography variant="caption" color="text.secondary">
              ({Object.values(permState).filter(Boolean).length} of {permissions.length} selected)
            </Typography>
          </Stack>
          {permsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading features…</Typography>
            </Box>
          ) : (
            <List dense disablePadding sx={{ maxHeight: 280, overflow: 'auto', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1 }}>
              {(permissions ?? []).map((p) => {
                const allowed = allowedFeatureIds.has(p.productFeatureId);
                return (
                  <ListItem key={p.productFeatureId} secondaryAction={
                    <Tooltip title={allowed ? '' : 'Not available under subscription tier'}>
                      <span>
                        <Checkbox
                          edge="end"
                          checked={!!permState[p.productFeatureId]}
                          onChange={(e) => setPermState(s => ({ ...s, [p.productFeatureId]: e.target.checked }))}
                          disabled={!allowed}
                        />
                      </span>
                    </Tooltip>
                  }>
                    <ListItemText primary={p.productFeatureName} />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" gutterBottom>Assign Users</Typography>
          {!assignedUsersLoaded ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading users…</Typography>
            </Box>
          ) : (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search users (name, username, email)"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    fullWidth
                  />
                  <ToggleButtonGroup
                    size="small"
                    color="primary"
                    exclusive
                    value={userFilter}
                    onChange={(_e, val) => { if (val) setUserFilter(val); }}
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="assigned">Assigned</ToggleButton>
                    <ToggleButton value="unassigned">Unassigned</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <List dense disablePadding sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1 }}>
                  {visibleUsers.map(u => {
                    const assigned = !!assignedUsers[String(u.id)];
                    return (
                      <ListItem key={u.id} sx={{ py: 0.25, pr: 12 }} secondaryAction={
                        assigned ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={unassigning}
                            onClick={async () => {
                              try {
                                await unassignUserRole({ roleId: [role.id], userId: String(u.id) }).unwrap();
                                setAssignedUsers(prev => ({ ...prev, [String(u.id)]: false }));
                              } catch {}
                            }}
                          >
                            Unassign
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={assigning}
                            onClick={async () => {
                              try {
                                await assignUserRole({ roleId: role.id, userId: [String(u.id)] }).unwrap();
                                setAssignedUsers(prev => ({ ...prev, [String(u.id)]: true }));
                              } catch {}
                            }}
                          >
                            Assign
                          </Button>
                        )
                      }>
                        <ListItemText
                          primaryTypographyProps={{ variant: 'body2', noWrap: true, title: u.fullName || u.userName || '-' }}
                          secondaryTypographyProps={{ variant: 'caption', noWrap: true, title: u.email ? `${u.userName} • ${u.email}` : u.userName }}
                          primary={u.fullName || u.userName || '-'}
                          secondary={u.email ? `${u.userName} • ${u.email}` : u.userName}
                        />
                      </ListItem>
                    );
                  })}
                  {!visibleUsers.length && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No users available.</Typography>
                  )}
                </List>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default DealerRolesTab;
